import { useState, useEffect } from "react";
import { authFetch } from "../lib/authFetch";
import {
  ShoppingCart, Loader2, AlertCircle, Calendar, Hash,
  Package, ChevronRight, X, ExternalLink, FileText,
} from "lucide-react";

interface LigneCommande {
  nom?: string;
  sku?: string;
  imageUrl?: string | null;
  nomFr?: string | null;
  quantite?: number;
  prixUnitaire?: number | null;
  prixTotal?: number | null;
  // Fallbacks pour d'autres formats éventuels
  name?: string;
  quantity?: number;
  unit_price?: number;
  item_total?: number;
  [key: string]: any;
}

interface Commande {
  id: string;
  zohoNumero: string | null;
  zohoId: string | null;
  dateCommande: string | null;
  dateLivraisonPrevue: string | null;
  statut: string | null;
  montantHt: number | null;
  montantTtc: number | null;
  devise: string | null;
  contactNom: string | null;
  lignes: LigneCommande[] | null;
  factureUrl: string | null;
  nbLignes: number;
  createdAt: string;
}

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatMontant(m: number | null, devise = "EUR") {
  if (m === null || m === undefined) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: devise || "EUR" }).format(m);
}

const STATUT_LABELS: Record<string, string> = {
  // Zoho Inventory
  draft: "Brouillon",
  confirmed: "Confirmé",
  en_cours: "En cours",
  en_attente: "En attente",
  packed: "En cours",
  shipped: "Expédié",
  delivered: "Livré",
  livree: "Livré",
  livre: "Livré",
  cancelled: "Annulé",
  annulee: "Annulé",
  annule: "Annulé",
  invoiced: "Facturé",
  facturee: "Facturé",
  facture: "Facturé",
  void: "Annulé",
};

const STATUT_COLORS: Record<string, string> = {
  draft: "bg-secondary text-muted-foreground",
  confirmed: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  en_cours: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  en_attente: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  packed: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  shipped: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  delivered: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  livree: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  livre: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  annulee: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  annule: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  invoiced: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  facturee: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  facture: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  void: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

function StatutBadge({ statut }: { statut: string | null }) {
  if (!statut) return <span className="text-muted-foreground text-xs">—</span>;
  const key = statut.toLowerCase().trim();
  const label = STATUT_LABELS[key] ?? statut;
  const color = STATUT_COLORS[key] ?? "bg-secondary text-muted-foreground";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function DetailPanel({ commande, onClose }: { commande: Commande; onClose: () => void }) {
  const lignes: LigneCommande[] = Array.isArray(commande.lignes) ? commande.lignes : [];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl bg-background border-l border-border shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-5 h-5 text-primary" />
            <div>
              <p className="font-semibold text-foreground">
                {commande.zohoNumero ?? `Commande #${commande.id.slice(0, 8)}`}
              </p>
              <p className="text-xs text-muted-foreground">{formatDate(commande.dateCommande)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            data-testid="btn-close-detail"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Infos générales */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Statut</p>
              <StatutBadge statut={commande.statut} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Montant HT</p>
              <p className="font-semibold text-foreground">{formatMontant(commande.montantHt, commande.devise ?? "EUR")}</p>
            </div>
            {commande.montantTtc && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Montant TTC</p>
                <p className="text-sm text-foreground">{formatMontant(commande.montantTtc, commande.devise ?? "EUR")}</p>
              </div>
            )}
            {commande.dateLivraisonPrevue && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Livraison prévue</p>
                <p className="text-sm text-foreground">{formatDate(commande.dateLivraisonPrevue)}</p>
              </div>
            )}
            {commande.contactNom && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground mb-1">Contact</p>
                <p className="text-sm text-foreground">{commande.contactNom}</p>
              </div>
            )}
            {commande.zohoId && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground mb-1">Référence Zoho</p>
                <p className="text-sm font-mono text-foreground">{commande.zohoId}</p>
              </div>
            )}
          </div>

          {commande.factureUrl && (
            <a
              href={commande.factureUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <FileText className="w-4 h-4" />
              Voir la facture
              <ExternalLink className="w-3 h-3" />
            </a>
          )}

          {/* Lignes de commande */}
          {lignes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-primary" />
                Lignes de commande ({lignes.length})
              </h3>
              <div className="space-y-2">
                {lignes.map((l, i) => {
                  const qty = l.quantite ?? l.quantity ?? 1;
                  const unitPrice = l.prixUnitaire ?? l.unit_price ?? null;
                  const total = l.prixTotal ?? l.item_total ?? (unitPrice !== null ? unitPrice * qty : null);
                  const label = l.nom ?? l.name ?? l.sku ?? `Ligne ${i + 1}`;
                  const hasSku = l.sku && l.sku.trim() !== "";
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border"
                      data-testid={`row-ligne-${i}`}
                    >
                      {/* Miniature produit 48×48 */}
                      <div className="shrink-0 w-12 h-12 rounded-md overflow-hidden bg-muted border border-border flex items-center justify-center">
                        {l.imageUrl ? (
                          <img
                            src={l.imageUrl}
                            alt={label}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Package className="w-5 h-5 text-muted-foreground/50" />
                        )}
                      </div>

                      {/* Infos produit */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate leading-tight">{label}</p>
                        {hasSku && (
                          <p className="text-xs text-muted-foreground font-mono mt-0.5">{l.sku}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-0.5">Qté&nbsp;: {qty}</p>
                      </div>

                      {/* Montants */}
                      <div className="text-right shrink-0">
                        {unitPrice !== null && (
                          <p className="text-xs text-muted-foreground">
                            {formatMontant(unitPrice, "EUR")}&nbsp;/&nbsp;u
                          </p>
                        )}
                        {total !== null && (
                          <p className="text-sm font-semibold text-foreground">
                            {formatMontant(total, "EUR")}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {lignes.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Package className="w-4 h-4" />
              Détail des lignes non disponible
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommandesPage() {
  const [commandes, setCommandes] = useState<Commande[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Commande | null>(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await authFetch("/api/portail/commandes").then(r => r.json());
      if (!Array.isArray(data)) throw new Error(data?.message ?? "Erreur serveur");
      setCommandes(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-primary" />
            Commandes
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Historique de vos 50 dernières commandes
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-md hover:bg-muted"
          data-testid="btn-refresh-commandes"
        >
          <Loader2 className={`w-4 h-4 ${loading ? "animate-spin" : "hidden"}`} />
          Actualiser
        </button>
      </div>

      {/* Loading */}
      {loading && !commandes && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm">Erreur de chargement</p>
            <p className="text-sm opacity-80 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && commandes?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ShoppingCart className="w-12 h-12 text-muted-foreground/30 mb-4" />
          <p className="text-base font-medium text-muted-foreground">Aucune commande trouvée</p>
          <p className="text-sm text-muted-foreground/70 mt-1">
            Vos premières commandes apparaîtront ici.
          </p>
        </div>
      )}

      {/* Table */}
      {commandes && commandes.length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden bg-card shadow-sm">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                    <span className="flex items-center gap-1"><Hash className="w-3.5 h-3.5" />N° commande</span>
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Date</span>
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Statut</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Montant HT</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Lignes</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Réf. Zoho</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {commandes.map((cmd, idx) => (
                  <tr
                    key={cmd.id}
                    onClick={() => setSelected(cmd)}
                    className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                    data-testid={`row-commande-${cmd.id}`}
                  >
                    <td className="px-4 py-3 font-medium text-foreground">
                      {cmd.zohoNumero ?? `#${cmd.id.slice(0, 8)}`}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(cmd.dateCommande)}</td>
                    <td className="px-4 py-3">
                      <StatutBadge statut={cmd.statut} />
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-foreground">
                      {formatMontant(cmd.montantHt, cmd.devise ?? "EUR")}
                    </td>
                    <td className="px-4 py-3 text-center text-muted-foreground">
                      {cmd.nbLignes > 0 ? cmd.nbLignes : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {cmd.zohoId ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-border">
            {commandes.map((cmd) => (
              <button
                key={cmd.id}
                onClick={() => setSelected(cmd)}
                className="w-full text-left px-4 py-4 hover:bg-muted/30 transition-colors flex items-start justify-between gap-3"
                data-testid={`card-commande-${cmd.id}`}
              >
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="font-medium text-foreground text-sm">
                    {cmd.zohoNumero ?? `#${cmd.id.slice(0, 8)}`}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatDate(cmd.dateCommande)}</p>
                  <StatutBadge statut={cmd.statut} />
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold text-foreground text-sm">
                    {formatMontant(cmd.montantHt, cmd.devise ?? "EUR")}
                  </p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto mt-1" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <DetailPanel commande={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
